import React from 'react';
import { useSelector } from 'react-redux';
import { Forms } from '@salesperformanceint/e4shared';
import { apiGet } from 'services';

import styles from './quickCheckSetter.module.css';

export const QuickCheckSetter = ({ value, onChange, st, error }) => {
    const parseValue = value => {
        const defaultState = { choice: 'program', launchOnPathwayCompletion: true, courseIds: [] };
        if (!value) return { result: defaultState, defaulted: true };
        try {
            const parsed = typeof value === 'string' ? JSON.parse(value) : value;
            const result = { ...defaultState, ...parsed };
            const defaulted = Object.keys(defaultState).some(key => !(key in parsed));
            return { result, defaulted };
        } catch {
            return { result: defaultState, defaulted: true };
        }
    };

    const initialValueRef = React.useRef(parseValue(value));
    // if the incoming value was missing properties, force the effect below to fire on mount so
    // the caller's onChange receives the normalised value (with defaults filled in)
    const isMountedRef = React.useRef(initialValueRef.current.defaulted);

    const [choice, setChoice] = React.useState(() => initialValueRef.current.result.choice);
    const [selectedProgram, setSelectedProgram] = React.useState(
        () => initialValueRef.current.result.courseIds?.[0] || null
    );

    const [launchOnPathwayCompletion, setLaunchOnPathwayCompletion] = React.useState(
        () => initialValueRef.current.result.launchOnPathwayCompletion
    );

    const [programs, setPrograms] = React.useState([]);

    const user = useSelector(state => state.user.user);

    const api = {
        loadPrograms: () => {
            if (choice === 'program' && !programs.length)
                apiGet('courses/quickcheck/program', user.guid, response => {
                    setPrograms(response.map(d => ({ id: d.id, name: d.list_Name })));
                });
        },
    };

    React.useEffect(() => {
        api.loadPrograms();
    }, [choice]);

    React.useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            return;
        }
        
        const payload = {
            choice,
            launchOnPathwayCompletion,
            courseIds: choice === 'program' ? [selectedProgram] : [],
        };

        onChange(JSON.stringify(payload));
    }, [choice, selectedProgram, launchOnPathwayCompletion]);

    const text = {
        choice: {
            label: st('QuickCheck/Style/Label'),
            description: st('QuickCheck/Style/Description'),
        },
        program: {
            label: st('QuickCheck/Style/Program/Label'),
            description: st('QuickCheck/Style/Program/Description'),
            choice: {
                label: st('QuickCheck/Style/Program/Choice/Label'),
            },
            launchStyle: {
                label: st('QuickCheck/Style/Program/LaunchStyle/Label'),
                help: st('QuickCheck/Style/Program/LaunchStyle/help'),
            },
        },
        module: {
            label: st('QuickCheck/Style/Module/Label'),
            description: st('QuickCheck/Style/Module/Description'),
        },
    };

    const options = [
        {
            value: 'program',
            label: text.program.label,
            description: text.program.description,
        },
        {
            value: 'module',
            label: text.module.label,
            description: text.module.description,
        },
    ];

    return (
        <div data-test-id="pathway-quickcheck-setter" className={error ? 'has-error' : ''}>
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                <legend className={styles.legend}>{text.choice.label}</legend>
                <p className={styles.subtitle}>{text.choice.description}</p>
                <div className={styles.grid}>
                    {options.map(option => (
                        <label
                            key={option.value}
                            className={`${styles.card} ${choice === option.value ? styles.cardSelected : ''}`}
                        >
                            <input
                                type="radio"
                                name="quickcheck_style"
                                value={option.value}
                                data-choice={choice}
                                checked={choice === option.value}
                                onChange={() => setChoice(option.value)}
                            />
                            <div
                                className={`${styles.cardInner} ${choice === option.value ? styles.cardInnerSelected : ''}`}
                            >
                                <div className={styles.cardHeader}>
                                    <span className={styles.cardTitle}>{option.label}</span>
                                    <div
                                        className={`${styles.radioIndicator} ${choice === option.value ? styles.radioIndicatorSelected : ''}`}
                                    />
                                </div>
                                <p className={styles.cardDesc}>{option.description}</p>
                            </div>
                        </label>
                    ))}
                </div>
            </fieldset>

            {choice === 'program' && (
                <>
                    <div className="mt5">
                        <Forms.Select
                            label={text.program.choice.label}
                            data={programs}
                            allowMultiple={false}
                            value={selectedProgram}
                            onChange={setSelectedProgram}
                        />
                        <Forms.Toggle
                            data-testid="field-schedule-quickcheck-toggle"
                            label={text.program.launchStyle.label}
                            help={text.program.launchStyle.help}
                            mandatory
                            onChange={setLaunchOnPathwayCompletion}
                            value={launchOnPathwayCompletion}
                        />
                    </div>
                </>
            )}

            {error && typeof error !== 'boolean' && (
                <span className="help-block">{typeof error === 'function' ? error() : error}</span>
            )}
        </div>
    );
};
